<template>
  <div>
    <div class="d-flex align-center" style="min-height: 40px">
      <b>{{ $t('PROJECT_SHEET.BUDGET') }}</b>
    </div>

    <v-card v-if="hasBudgetSummary" class="pa-6 d-flex align-center justify-space-evenly gap-2">
      <div class="flex-center-center" style="height: 140px;">
        <chart-js :config="graphicChart" :width="140" :height="140"></chart-js>
        <i :class="`budget-${selectedBudgetIcon}`" style="position: absolute; font-size: larger;"></i>
      </div>
      <div class="text-center d-flex flex-column justify-center" style="height: 140px; min-width: 0;">
        <div class="my-auto d-flex flex-wrap gap-2 justify-space-evenly line-height-comfortable">
          <div>
            <div class="font-weight-bold" style="font-size: x-large">
              {{ $filters.number(Math.round(selectedBudgetStateTotal / selectedBudgetStateMultiple.value)) }}{{ selectedBudgetStateMultiple.unit }}
            </div>
            {{ $t('PROJECT_SHEET.BUDGET_SPENT') }}
          </div>
          <div v-if="selectedBudgetStateRemaining">
            <div class="font-weight-bold" style="font-size: x-large">
              {{ $filters.number(Math.round(selectedBudgetStateRemaining / selectedBudgetStateMultiple.value)) }}{{ selectedBudgetStateMultiple.unit }}
            </div>
            {{ $t('PROJECT_SHEET.BUDGET_REMAINING') }}
          </div>
        </div>
        <v-btn variant="text" class="export-hidden" @click="$emit('open-project-report', 'budget')">
          {{ $t('PROJECT_SHEET.SEE_DETAILED_BUDGET') }}
          <v-icon size="small" class="ml-1">far fa-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-card>
    <v-card v-else class="pa-6 text-center text-medium-emphasis">
      {{ $t('PROJECT_SHEET.NO_BUDGET') }}
    </v-card>
  </div>
</template>

<script>
  import darkModeMixin from '@/components/Manage/ManageProfile/darkModeMixin';
  import ChartJs from '@/components/Reusables/ChartJs.vue';
  import useUsersAccessRightStore from '@/stores/users/accessRight';

  const budgetSummaryPalette = ['primary', 'success', 'error', 'info', 'warning', 'midgrey'];

  export default {
    components: {
      ChartJs,
    },
    mixins: [darkModeMixin],
    props: {
      planning: { type: Object, required: true },
      noAnimation: { type: Boolean, default: false },
    },
    emits: ['open-project-report'],
    data() {
      return {
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      graphicChart() {
        const series = this.selectedBudgetStates.slice();
        const colors = budgetSummaryPalette.slice(0, series.length).map(item => `${this.$vuetify.theme.current.colors[item]}66`); // adding opacity to the palette colors
        if (this.selectedBudgetStateRemaining > 0) {
          series.push({
            key: 'remaining',
            label: this.$t('PROJECT_SHEET.BUDGET_REMAINING'),
            amount: this.selectedBudgetStateRemaining,
          });
          colors.push(`${this.$vuetify.theme.current.colors.midgrey}66`);
        }

        return {
          type: 'doughnut',
          data: {
            labels: series.map(state => state.label),
            datasets: [{
              data: series.map(state => state.amount),
              backgroundColor: colors,
              borderColor: this.darkMode ? '#232323' : 'white',
              borderWidth: 2,
            }],
          },
          options: {
            ...(this.noAnimation ? { animation: false } : {}),
            cutout: '50%',
            rotation: -30,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: true,
              },
            },
          },
        };
      },
      hasBudgetSummary() {
        return this.selectedBudgetIcon !== null;
      },
      budgetStates() {
        return this.usersAccessRightStore.config.budgetStates;
      },
      budgetStateKeys() {
        return [...Object.keys(this.budgetStates), ''];
      },
      budgetStateKeysSet() {
        return new Set(this.budgetStateKeys);
      },
      budgetStateEntries() {
        return Object.entries(this.budgetStates);
      },
      filteredBudgetElements() {
        return (this.planning.elements || []).filter((element) => {
          if (element.isType('macro')) return false;
          const elementBudgets = element.getBudgets();
          if (! elementBudgets) return false;
          return elementBudgets.some(budget => budget.amount || budget.amount_inprogress);
        });
      },
      sumBudgetByIconsStates() {
        const budgetsByIconsStates = {};
        this.filteredBudgetElements.forEach((element) => {
          (element.getBudgets() || []).forEach((budget) => {
            const state = this.budgetStateKeysSet.has(budget.state) ? budget.state : '';
            const icon = budget.icon || '';
            if (! budget.amount && ! budget.amount_inprogress) return;
            if (! budgetsByIconsStates[icon]) budgetsByIconsStates[icon] = { totals: { totalAmount: 0, totalAmountInProgress: 0 } };
            if (! budgetsByIconsStates[icon][state]) budgetsByIconsStates[icon][state] = { amount: 0, amount_inprogress: 0 };
            budgetsByIconsStates[icon][state].amount += budget.amount || 0;
            budgetsByIconsStates[icon][state].amount_inprogress += budget.amount_inprogress || 0;
            budgetsByIconsStates[icon].totals.totalAmount += budget.amount || 0;
            budgetsByIconsStates[icon].totals.totalAmountInProgress += budget.amount_inprogress || 0;
          });
        });
        return budgetsByIconsStates;
      },
      selectedBudgetIcon() {
        return Object.keys(this.sumBudgetByIconsStates).reduce((accIcon, icon) => {
          if (accIcon === null) return icon; // first iteration
          const accMax = Math.max(this.sumBudgetByIconsStates[accIcon].totals.totalAmount || 0, this.sumBudgetByIconsStates[accIcon].totals.totalAmountInProgress || 0);
          const currentMax = Math.max(this.sumBudgetByIconsStates[icon].totals.totalAmount || 0, this.sumBudgetByIconsStates[icon].totals.totalAmountInProgress || 0);
          return currentMax > accMax ? icon : accIcon;
        }, null);
      },
      selectedBudgetStates() {
        if (this.selectedBudgetIcon === null) return [];
        const budgetByStates = this.sumBudgetByIconsStates[this.selectedBudgetIcon] || {};
        const states = this.budgetStateKeys.reduce((acc, key) => {
          const stateBudget = budgetByStates[key];
          const amount = Math.max(stateBudget?.amount || 0, stateBudget?.amount_inprogress || 0);
          if (! amount) return acc;
          acc.push({
            key,
            label: this.budgetStates[key] || '-',
            amount,
          });
          return acc;
        }, []);

        return states;
      },
      selectedBudgetStateTotal() {
        return this.selectedBudgetStates.reduce((sum, state) => sum + state.amount, 0);
      },
      selectedBudgetStateRemaining() {
        if (! this.planning.projectsheet?.target_budget) return null;
        return this.planning.projectsheet.target_budget - this.selectedBudgetStateTotal;
      },
      selectedBudgetStateMultiple() {
        if (this.selectedBudgetStateTotal >= 100000000 || this.selectedBudgetStateRemaining >= 100000000) return { value: 1000000, unit: 'M' };
        if (this.selectedBudgetStateTotal >= 100000 || this.selectedBudgetStateRemaining >= 100000) return { value: 1000, unit: 'k' };
        return { value: 1, unit: '' };
      },
    },
  };
</script>
